/**
 * useMeasurementController — Iter 36.5 Viewer Architecture Hardening.
 *
 * Owns ALL measurement state + interaction for SheetViewer:
 *   - mode ("off" | "measure" | "calibrate")
 *   - active tool ("distance" | "polyline" | "area" | "coordinate")
 *   - in-progress points + hover preview
 *   - reload-key bump on successful auto-save
 *   - 4-mode click router with completion gestures (tap-tap / close-polygon /
 *     double-tap / single-tap) and offline enqueue fallback.
 *
 * Behavior is IDENTICAL to the inline code that previously lived in
 * SheetViewer.jsx — this is a pure extraction. The hook is the seam where
 * Utility Intelligence (future "snap to crossing") will plug in without
 * touching the viewer page itself.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { confirm } from "@/lib/confirm";
import { useSheetScale } from "@/components/MeasureTool";
import { distanceFt, polygonAreaSf, angleDegrees, arcLengthFt, polylineLengthFt } from "@/lib/sheetScale";
import { resolveScaleForPoints } from "@/lib/scaleResolution";
import { enqueue as enqueueSync, drainNow as drainSync } from "@/lib/syncQueue";
import { useUnitPreference } from "@/contexts/UnitPreferenceContext";
import { formatLength, formatAreaValue } from "@/lib/units";
import { loadFailedDrafts, upsertFailedDraft, removeFailedDraft, subscribeToFailedDrafts } from "@/lib/failedMeasurementDrafts";


export function useMeasurementController({ sheetId, gpsTransform, imgDims, scaleContext, identityKey, scaleRegions }) {
  // measureMode: "off" | "measure" | "calibrate"
  // toolMode: "distance" | "polyline" | "area" | "coordinate" | "radius" |
  //           "diameter" | "angle" | "arc" | "count"
  const [measureMode, setMeasureMode] = useState("off");
  const [toolMode, setToolMode] = useState("distance");
  const [measurePts, setMeasurePts] = useState([]);
  const [measureHover, setMeasureHover] = useState(null);
  const { scale: sheetScale, save: saveSheetScale, clear: clearSheetScale } = useSheetScale(sheetId);
  // R-12 (independent recheck batch 2, 2026-10-01) — this used to call
  // its OWN separate `useSheetScaleRegions(sheetId)`, a SECOND,
  // independent fetch/state copy of the exact same `/sheets/{id}/scales`
  // data SheetViewer.jsx already owns (for the region-box draw/edit UI
  // and its own `liveScale` live-preview memo). Two independent React
  // hook instances for the SAME sheet never know about each other's
  // `create`/`update`/`remove` calls: editing a region through
  // SheetViewer's copy reloaded ONLY that copy — this hook's own
  // internal copy stayed silently stale until the whole page remounted.
  // A measurement completed in that window would resolve against
  // calibration data that no longer existed. There is now exactly ONE
  // `/sheets/{id}/scales` consumer per sheet (SheetViewer owns it,
  // every create/update/remove already reloads it) — this hook just
  // receives that SAME list as a parameter, so it is architecturally
  // impossible for the two to disagree.
  const resolveScale = useCallback(
    (points) => resolveScaleForPoints(scaleRegions || [], points, scaleContext).scale,
    [scaleRegions, scaleContext],
  );
  const [measurementsReloadKey, setMeasurementsReloadKey] = useState(0);
  const { lengthUnit, areaUnit } = useUnitPreference();
  // GH-31 (real-plan audit, 2026-10-01) — the backend's Scale Trust Gate
  // (routers/measurements.py) refuses a NEW value on an "untrusted" (NTS
  // — Not To Scale) sheet with a 409 `scale_not_to_scale` unless the
  // request explicitly sets `acknowledge_scale_risk`. Nothing in the
  // frontend ever read that code: the generic catch-all below showed a
  // bare "Couldn't save measurement · Conflict" toast whose Retry button
  // resent the EXACT same payload — a dead loop with no way to actually
  // acknowledge the risk and get the measurement saved. Holds the
  // pending payload + the backend's own explanation so a real
  // acknowledgment dialog (rendered by SheetViewer) can resend the SAME
  // payload with the flag set, or discard it.
  const [ntsPrompt, setNtsPrompt] = useState(null);

  // R-10 (independent recheck, 2026-10-01) — `confirm.saveFailed`'s toast
  // is a 7-second UI hint, not storage. When the network save, the
  // offline durable queue, AND localStorage can all genuinely fail
  // (B2-03), the only place the measurement's points/label/unit lived
  // was that toast's closure — gone the moment it timed out or the
  // operator navigated away, with no way back short of redrawing from
  // memory. `failedDrafts` is an identity-scoped (B2-02), concurrency-
  // safe (B2-04), cross-tab-synced persistence layer — see
  // `lib/failedMeasurementDrafts.js` for the full design. We never
  // claim this survives a full browser crash; `persisted` on each
  // draft (false when even localStorage rejected the write) says so
  // explicitly instead of implying a guarantee that isn't real.
  const [failedDrafts, setFailedDrafts] = useState(() => loadFailedDrafts(identityKey));

  // B2-02 — re-load whenever the SIGNED-IN IDENTITY changes (login/
  // logout/switch-account on this device) so one identity's drafts are
  // never shown while a different identity is active.
  useEffect(() => {
    setFailedDrafts(loadFailedDrafts(identityKey));
  }, [identityKey]);

  // B2-04 — a change made in ANOTHER tab (or another mounted viewer
  // instance) under the SAME identity must show up here too, not just
  // overwrite it on the next unrelated write.
  useEffect(() => {
    return subscribeToFailedDrafts(identityKey, setFailedDrafts);
  }, [identityKey]);

  const addFailedDraft = useCallback((payload, targetSheetId, reason) => {
    if (!identityKey) return;
    upsertFailedDraft(identityKey, payload, targetSheetId, reason).then(({ drafts }) => {
      setFailedDrafts(drafts);
    });
  }, [identityKey]);

  const discardFailedDraft = useCallback((id) => {
    if (!identityKey) return;
    removeFailedDraft(identityKey, id).then(({ drafts }) => {
      setFailedDrafts(drafts);
    });
  }, [identityKey]);

  // B2-03 — "navigation protection" for memory-only drafts (persisted:
  // false) now lives app-wide in `UnsavedDraftsBanner.jsx` (mounted in
  // `Shell.jsx`, which outlives this hook across route changes — see
  // B3-04 file header in `failedMeasurementDrafts.js`). A per-viewer
  // `beforeunload` listener here would be torn down the instant the
  // operator navigates to another page, which is exactly the gap the
  // app-wide banner exists to close.


  // Reset measurement state when the sheet changes.
  useEffect(() => {
    setMeasurePts([]);
    setMeasureHover(null);
    setMeasureMode("off");
    setToolMode("distance");
  }, [sheetId]);

  const startMeasure = useCallback(() => {
    setMeasureMode("measure"); setMeasurePts([]); setMeasureHover(null);
  }, []);
  const startCalibrate = useCallback(() => {
    setMeasureMode("calibrate"); setMeasurePts([]); setMeasureHover(null);
  }, []);
  // Connected-workflow program (2026-09-10) — detail-region calibration
  // step 1: draw the region's bounding box (2 opposite-corner taps)
  // before entering the ordinary 2-point distance calibration. Reuses
  // the exact same generic tap-capture fallback "calibrate" already
  // relies on (see the else-branch below) — a mode value just has to
  // not match "measure" for a tap to append to measurePts instead of
  // completing a shape.
  const startRegionBox = useCallback(() => {
    setMeasureMode("region_box"); setMeasurePts([]); setMeasureHover(null);
  }, []);
  const closeMeasure = useCallback(() => {
    setMeasureMode("off"); setMeasurePts([]); setMeasureHover(null); setToolMode("distance");
  }, []);

  // Iter 33 — persistent measurements. Posts to /measurements; on offline /
  // transient failure falls back to the Iter 36 sync queue so the user
  // never sees an error and the measurement is preserved.
  //
  // Iter 139 (MASCI fix) — when the backend refuses with the new
  // `sheet_not_calibrated` 400, surface a clear toast prompting the user
  // to set the sheet scale first. The old silent path stored value=null
  // and rendered "—", which looked like the tool was broken.
  const autoSaveMeasurement = useCallback(async (payload, targetSheetIdOverride) => {
    // R-10 — retrying a persisted draft from `failedDrafts` must post to
    // the SHEET IT WAS DRAWN ON, not whichever sheet happens to be open
    // right now (the operator may have navigated elsewhere and back).
    const targetSheetId = targetSheetIdOverride || sheetId;
    if (!targetSheetId) return;
    // GH-21 (real-plan certification, 2026-10-01) — stable client
    // operation identity, generated ONCE per measurement and reused
    // across every retry of the SAME payload (the initial online
    // attempt, any `confirm.saveFailed` retry, and the offline queue's
    // own later replay all share this exact object reference, so
    // mutating it here persists the id everywhere this measurement is
    // ever resent). Lets the server recognize "this is a retry of a
    // create that may have already committed" instead of always
    // inserting a new Measurement and doubling the takeoff quantity.
    if (!payload.client_op_id) {
      payload.client_op_id = `msr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    }
    try {
      await api.post(`/sheets/${targetSheetId}/measurements`, payload, { timeout: 8000 });
      // R-10 — this payload may have been sitting in `failedDrafts`
      // (e.g. the operator navigated back and tapped Retry from the
      // persistent banner, not the original toast). A successful save
      // means the draft is no longer "failed" — clear it.
      discardFailedDraft(payload.client_op_id);
      if (targetSheetId === sheetId) setMeasurementsReloadKey((k) => k + 1);
      // PILLAR 2 — confirm every measurement save. Quiet toast, 2.4s.
      // The operator should never wonder "did that save?".
      confirm.measurementSaved(payload?.label);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 400 && typeof detail === "object" && detail?.code === "sheet_not_calibrated") {
        // B3-05 (2026-10-01 completion directive) — every OTHER
        // rejection branch below preserves the drawn geometry via
        // `addFailedDraft`; this one used to just show a toast and
        // `return`, discarding the payload outright. A calibration
        // rejection is exactly as recoverable as any other — the
        // operator calibrates the sheet, then retries the SAME draft
        // (same points/value/unit/client_op_id), which now saves
        // normally and recomputes correctly under the new scale.
        addFailedDraft(payload, targetSheetId, "Sheet not calibrated — calibrate the sheet, then retry this draft.");
        // PILLAR 2 — every failure must say what to do next.
        confirm.needsCalibration(() => autoSaveMeasurement(payload, targetSheetId));
        return;
      }
      // GH-31 / R-11 (independent recheck, 2026-10-01) — Scale Trust
      // Gate 409. Surface the backend's own NTS explanation and let
      // the operator either acknowledge (resend the SAME payload, now
      // flagged) or discard. This must come BEFORE the generic
      // `if (status)` branch below, which would otherwise swallow it
      // into an unreachable "Retry". `originSheetId` binds this prompt
      // to the EXACT sheet these points were drawn on — the operator
      // can navigate to a different sheet while this dialog is still
      // pending (nothing here clears it on sheet change), and when
      // they finally tap Acknowledge, the save must still land on the
      // sheet the geometry actually belongs to, never whatever sheet
      // happens to be open at that later moment.
      if (status === 409 && typeof detail === "object" && detail?.code === "scale_not_to_scale") {
        setNtsPrompt({ payload, message: detail.message, originSheetId: targetSheetId });
        return;
      }
      // Iter 179 — Any OTHER server rejection (4xx/5xx WITH a response)
      // is a real validation problem; don't hide it in the offline queue.
      // Only true network-level failures (no HTTP response at all) fall
      // through to the queue path so pending work still persists offline.
      //
      // B2-07 (independent recheck batch 2, 2026-10-01) — this branch
      // used to ONLY show the 7-second toast, same gap as the
      // double-failure case R-10 already fixed: a transient 5xx (or any
      // other response-bearing failure) lost the drawn geometry the
      // instant the toast timed out or the operator navigated away. A
      // server error is not a reason to discard field geometry — persist
      // it exactly like the double-failure path. This is still a
      // MANUAL, operator-clicked retry (never an automatic background
      // replay), so retaining a genuinely-invalid 4xx payload here is
      // safe — it is never blindly re-sent on its own.
      if (status) {
        const reason = status >= 500
          ? `Server error (${status}) — the server had a problem saving this. Kept as a draft, tap retry.`
          : `Rejected (${status}) — ${typeof detail === "string" ? detail : "the server did not accept this measurement."} Kept as a draft; correct it or discard.`;
        addFailedDraft(payload, targetSheetId, reason);
        confirm.saveFailed("Couldn't save measurement", {
          error: e,
          recovery: reason,
          retry: () => autoSaveMeasurement(payload, targetSheetId),
        });
        return;
      }
      try {
        const queued = await enqueueSync({
          op_type: "measurement.create",
          endpoint: `/sheets/${targetSheetId}/measurements`,
          method: "POST",
          payload,
          scope_key: `measurements:sheet:${targetSheetId}`,
        });
        if (!queued) {
          // GH-20 / R-10 — `enqueue()` signals a genuine durable-storage
          // failure with `null` instead of claiming success. Both the
          // network save AND durable storage failed: do NOT pretend
          // this measurement is safe anywhere. Persist the full payload
          // (points, value, label, unit) to `failedDrafts` — a small
          // localStorage-backed list that survives navigation and page
          // reload, independent of the 7-second toast — so the operator
          // can come back later, from any sheet, and retry or discard it
          // without having to redraw it from memory.
          addFailedDraft(payload, targetSheetId, "Offline storage is unavailable on this device.");
          confirm.saveFailed("Measurement not saved", {
            recovery: "Offline storage is unavailable on this device — kept as a draft, tap retry.",
            retry: () => autoSaveMeasurement(payload, targetSheetId),
          });
          return;
        }
        drainSync();
        if (targetSheetId === sheetId) setMeasurementsReloadKey((k) => k + 1);
        // PILLAR 2 — measurement is preserved offline; tell the operator.
        confirm.offline();
      } catch {
        // Defensive fallback — `enqueue()` itself is not expected to
        // throw anymore (it signals failure via `null`), but never
        // silently drop the measurement if it somehow does.
        addFailedDraft(payload, targetSheetId, "Save failed unexpectedly.");
        confirm.saveFailed("Measurement not saved", {
          recovery: "Kept as a draft — tap retry, or check your connection.",
          retry: () => autoSaveMeasurement(payload, targetSheetId),
        });
      }
    }
  }, [sheetId, addFailedDraft, discardFailedDraft]);

  const retryFailedDraft = useCallback((id) => {
    const draft = failedDrafts.find((d) => d.id === id);
    if (!draft) return;
    autoSaveMeasurement(draft.payload, draft.sheetId);
  }, [failedDrafts, autoSaveMeasurement]);

  const copyFailedDraft = useCallback(async (id) => {
    const draft = failedDrafts.find((d) => d.id === id);
    if (!draft) return false;
    const text = JSON.stringify(draft.payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }, [failedDrafts]);

  // Click → place point. Each tool's completion gesture fires
  // autoSaveMeasurement and resets the in-progress points.
  const onMeasureClick = useCallback((nx, ny) => {
    setMeasureHover(null);

    // --- Coordinate: single tap → save (if GPS-ready, attach lat/lon) ---
    if (toolMode === "coordinate" && measureMode === "measure") {
      const pt = { nx, ny };
      setMeasurePts([pt]);
      const geo = gpsTransform?.sheetToGps ? gpsTransform.sheetToGps(nx, ny) : null;
      const label = geo ? `${geo.lat.toFixed(6)}, ${geo.lng.toFixed(6)}` : "no GPS calibration";
      autoSaveMeasurement({
        kind: "coordinate",
        points: [pt],
        unit: "coord",
        label,
        coordinate: geo ? { lat: geo.lat, lon: geo.lng } : null,
      });
      setTimeout(() => setMeasurePts([]), 350);
      return;
    }

    // --- Count: tap adds a tally marker; no auto-finish gesture — the
    // operator taps "Finish" (finishCount, below) when done counting.
    if (toolMode === "count" && measureMode === "measure") {
      setMeasurePts((prev) => [...prev, { nx, ny }]);
      return;
    }

    // --- Angle: A → vertex → B, 3 taps, auto-saves on the 3rd ---
    if (toolMode === "angle" && measureMode === "measure") {
      if (measurePts.length === 2) {
        const next = [...measurePts, { nx, ny }];
        const deg = angleDegrees(next, imgDims);
        autoSaveMeasurement({
          kind: "angle",
          points: next,
          value: deg,
          unit: "deg",
          label: deg != null ? `${deg.toFixed(1)}\u00b0` : "—",
        });
        setMeasurePts(next);
        setTimeout(() => setMeasurePts([]), 350);
        return;
      }
      setMeasurePts((prev) => [...prev, { nx, ny }]);
      return;
    }

    // --- Arc: tap ≥3 points; tap near the last point again = finish ---
    if (toolMode === "arc" && measureMode === "measure") {
      const last = measurePts[measurePts.length - 1];
      if (last && measurePts.length >= 3) {
        const d = Math.hypot(nx - last.nx, ny - last.ny);
        if (d < 0.025) {
          const ft = arcLengthFt(resolveScale(measurePts), measurePts);
          autoSaveMeasurement({
            kind: "arc",
            points: measurePts,
            value: ft,
            unit: lengthUnit,
            label: ft != null ? formatLength(ft, lengthUnit) : `${measurePts.length} pts`,
          });
          setMeasurePts([]);
          return;
        }
      }
      setMeasurePts((prev) => [...prev, { nx, ny }]);
      return;
    }

    // --- Polyline: tap to append; tap within ~2.5% of last point = finish ---
    if (toolMode === "polyline" && measureMode === "measure") {
      const last = measurePts[measurePts.length - 1];
      if (last && measurePts.length >= 2) {
        const d = Math.hypot(nx - last.nx, ny - last.ny);
        if (d < 0.025) {
          const ft = polylineLengthFt(resolveScale(measurePts), measurePts);
          autoSaveMeasurement({
            kind: "polyline",
            points: measurePts,
            value: ft,
            unit: lengthUnit,
            label: ft != null ? formatLength(ft, lengthUnit) : `${measurePts.length} pts`,
          });
          setMeasurePts([]);
          return;
        }
      }
      setMeasurePts((prev) => [...prev, { nx, ny }]);
      return;
    }

    // --- Area: tap to append; tap on first vertex closes polygon ---
    if (toolMode === "area" && measureMode === "measure") {
      const first = measurePts[0];
      if (first && measurePts.length >= 3) {
        const dist = Math.hypot(nx - first.nx, ny - first.ny);
        if (dist < 0.025) {
          const sf = polygonAreaSf(resolveScale(measurePts), measurePts);
          autoSaveMeasurement({
            kind: "area",
            points: measurePts,
            value: sf,
            unit: areaUnit,
            label: sf != null ? formatAreaValue(sf, areaUnit) : `${measurePts.length} pts`,
          });
          setMeasurePts([]);
          return;
        }
      }
      setMeasurePts((prev) => [...prev, { nx, ny }]);
      return;
    }

    // --- Distance / radius / diameter / calibrate: 2-point tap-tap ---
    // autoSave is called OUTSIDE the state updater because StrictMode
    // invokes updater functions twice, which would double-post.
    if (
      measureMode === "measure"
      && (toolMode === "distance" || toolMode === "radius" || toolMode === "diameter")
      && measurePts.length === 1
    ) {
      const next = [measurePts[0], { nx, ny }];
      const ft = distanceFt(resolveScale(next), next[0], next[1]);
      const prefix = toolMode === "radius" ? "R=" : toolMode === "diameter" ? "\u2300=" : "";
      autoSaveMeasurement({
        kind: toolMode,
        points: next,
        value: ft,
        unit: lengthUnit,
        label: ft != null ? `${prefix}${formatLength(ft, lengthUnit)}` : "—",
      });
      setMeasurePts(next);
      setTimeout(() => setMeasurePts([]), 350);
      return;
    }
    setMeasurePts((prev) => {
      if (prev.length === 0) return [{ nx, ny }];
      if (prev.length === 1) return [...prev, { nx, ny }];
      return [{ nx, ny }];
    });
  }, [toolMode, measureMode, measurePts, resolveScale, gpsTransform, autoSaveMeasurement, lengthUnit, areaUnit, imgDims]);

  // Count tool has no proximity-finish gesture (taps are scattered, not
  // sequential) — the operator explicitly finishes the tally.
  const finishCount = useCallback(() => {
    if (toolMode !== "count" || measurePts.length === 0) return;
    autoSaveMeasurement({
      kind: "count",
      points: measurePts,
      value: measurePts.length,
      unit: "count",
      label: `${measurePts.length} item${measurePts.length === 1 ? "" : "s"}`,
    });
    setMeasurePts([]);
  }, [toolMode, measurePts, autoSaveMeasurement]);

  const onMeasureHover = useCallback((nx, ny) => {
    if (toolMode === "area" && measurePts.length >= 1) {
      setMeasureHover({ nx, ny });
    } else if (toolMode === "polyline" && measurePts.length >= 1) {
      setMeasureHover({ nx, ny });
    } else if (toolMode === "arc" && measurePts.length >= 1) {
      setMeasureHover({ nx, ny });
    } else if ((toolMode === "distance" || toolMode === "radius" || toolMode === "diameter") && measurePts.length === 1) {
      setMeasureHover({ nx, ny });
    } else if (toolMode === "angle" && measurePts.length >= 1 && measurePts.length < 3) {
      setMeasureHover({ nx, ny });
    }
  }, [toolMode, measurePts]);

  const undoMeasurePoint = useCallback(() => {
    setMeasurePts((prev) => prev.slice(0, -1));
    setMeasureHover(null);
  }, []);

  const clearPoints = useCallback(() => {
    setMeasurePts([]);
    setMeasureHover(null);
  }, []);

  const setToolModeAndReset = useCallback((t) => {
    setToolMode(t);
    setMeasurePts([]);
    setMeasureHover(null);
  }, []);

  // MEDIUM fix (real-plan cert iteration_184) — deleting a measurement
  // from the History drawer removed it server-side but the status-bar
  // MEASURE count and the on-canvas overlay stayed stale until a full
  // reload (only the save path bumped this key before). Exposed so any
  // delete surface can force both to refetch.
  const bumpMeasurementsReloadKey = useCallback(() => {
    setMeasurementsReloadKey((k) => k + 1);
  }, []);

  // GH-31 / R-11 — called by the acknowledgment dialog. `accepted=true`
  // resends the EXACT pending payload (same client_op_id, so a
  // double-click or a retried acknowledge never double-creates) with
  // the risk flag set, targeted at `originSheetId` — NOT whatever
  // sheet is current right now — so a late acknowledgment after
  // navigating away never saves sheet-A geometry onto sheet B.
  // `accepted=false` discards it and the measurement is simply not
  // saved — never silently retried, never silently dropped. The save
  // side effect is a plain statement here, not nested inside the
  // `setNtsPrompt` updater (React may invoke a state updater more than
  // once, e.g. under Strict Mode double-invocation, and a side effect
  // belongs in the callback body, not the reducer).
  const resolveNtsPrompt = useCallback((accepted) => {
    const prompt = ntsPrompt;
    setNtsPrompt(null);
    if (accepted && prompt) {
      autoSaveMeasurement({ ...prompt.payload, acknowledge_scale_risk: true }, prompt.originSheetId);
    }
  }, [ntsPrompt, autoSaveMeasurement]);

  return {
    // State
    measureMode,
    toolMode,
    measurePts,
    measureHover,
    sheetScale,
    measurementsReloadKey,
    lengthUnit,
    areaUnit,
    ntsPrompt,
    failedDrafts,
    // Actions
    startMeasure,
    startCalibrate,
    startRegionBox,
    closeMeasure,
    onMeasureClick,
    onMeasureHover,
    undoMeasurePoint,
    clearPoints,
    finishCount,
    setToolMode: setToolModeAndReset,
    saveSheetScale,
    clearSheetScale,
    bumpMeasurementsReloadKey,
    resolveNtsPrompt,
    retryFailedDraft,
    discardFailedDraft,
    copyFailedDraft,
  };
}
